import { useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { doc } from "firebase/firestore";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { db } from "../../lib/firebase";
import { useFirestoreDocOnce } from "../../hooks/useFirestoreDocOnce";
import { pickedAudienceIdsForAudience } from "../../services/audienceService";
import { SessionForm } from "./SessionForm";
import type { Audience, Session } from "@event-planner/shared";

/**
 * Create page, with an optional duplicate mode: `?from=<sessionId>`
 * hydrates the form from an existing session (title, description,
 * category, times, location, audience picks, speakers, materials).
 * Nothing is written until the organizer hits Save.
 *
 * A query param (not router state) so the flow survives refresh and is
 * deep-linkable — and because router state structured-clones Firestore
 * Timestamps into plain objects, breaking `.toDate()`.
 */
export function SessionCreatePage() {
  const { eventId } = useParams<{ eventId: string }>();
  const [searchParams] = useSearchParams();
  const from = searchParams.get("from");

  const sourceRef = useMemo(
    () => (from ? doc(db, "sessions", from) : null),
    [from],
  );
  const { data: source, loading: sourceLoading } =
    useFirestoreDocOnce<Session>(sourceRef);

  // The source's linked audience, reverse-resolved into picker values.
  // Anonymous compositions spread their include list so saving mints a
  // FRESH composition (never shared with the source — deleteSession
  // cleans up anonymous audiences when their owner is deleted).
  const audienceRef = useMemo(
    () =>
      source?.audienceId ? doc(db, "audiences", source.audienceId) : null,
    [source?.audienceId],
  );
  const { data: audience, loading: audienceLoading } =
    useFirestoreDocOnce<Audience>(audienceRef);

  if (from && (sourceLoading || (source && audienceLoading))) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Guard: deleted source, or a deep link pointing at another event's
  // session — fall back to a plain blank create form.
  const validSource = source && source.eventId === eventId ? source : null;

  if (!validSource) {
    return <SessionForm mode="create" />;
  }

  // Deleted audience → [] → the form's min(1) validation forces a repick
  // (and the existing "no attendees will see this" alert shows).
  return (
    <SessionForm
      mode="create"
      initialData={validSource}
      initialPickedAudienceIds={pickedAudienceIdsForAudience(audience)}
    />
  );
}
